import type { ComponentProps, ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface FieldProps {
  /** Id of the control this label points at, so clicking the label focuses the input. */
  htmlFor?: string;
  label: ReactNode;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Label + control + optional hint on one rhythm. Labels are deliberately small and muted so the
 * entered value is the loudest thing in a dense pricing grid.
 */
export function Field({ htmlFor, label, hint, className, children }: FieldProps) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label
        htmlFor={htmlFor}
        className="text-xs font-medium text-muted-foreground"
      >
        {label}
      </Label>
      {children}
      {hint ? (
        <p className="text-xs leading-snug text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** Uppercase caption that splits a card into scannable sub-groups. */
export function SectionLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
    >
      {children}
    </p>
  );
}

/** Numeric input with a trailing unit (currency, GB, …) so wide inputs still read as a quantity. */
export function UnitInput({
  unit,
  className,
  ...props
}: ComponentProps<"input"> & { unit: string }) {
  return (
    <div className="relative">
      <Input {...props} className={cn("pr-14 tabular-nums", className)} />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-muted-foreground">
        {unit}
      </span>
    </div>
  );
}
